/**
 * Created: 2026-10-24
 * Initial commit: Export the 客户收支 page to an Excel file
 *
 * Builds the 客户收支 sheet with exceljs and downloads it: one row per payment
 * or refund, amounts as numbers in yuan, and a totals row.
 */

import { ORDER_TYPE_LABEL } from '@tc-app/shared/labels'
import { CustomerPaymentKind, type CustomerPaymentsQuery } from '../gql/graphql'

type PaymentRow = CustomerPaymentsQuery['customerPayments'][number]

const KIND_LABEL: Record<CustomerPaymentKind, string> = {
  [CustomerPaymentKind.Payment]: '支付',
  [CustomerPaymentKind.Refund]: '退款',
}

const YUAN = { numFmt: '"¥"#,##0.00' }

export async function exportCustomerPayments(
  rows: PaymentRow[],
  fileName: string,
): Promise<void> {
  // Loaded on demand: exceljs is large and only needed on export.
  const { Workbook } = await import('exceljs')
  const workbook = new Workbook()
  const sheet = workbook.addWorksheet('客户收支', {
    views: [{ state: 'frozen', ySplit: 1 }],
  })
  sheet.columns = [
    { header: '#', key: 'index', width: 6 },
    { header: '支付时间', key: 'time', width: 22 },
    { header: '类型', key: 'kind', width: 8 },
    { header: '订单类型', key: 'orderCategory', width: 10 },
    { header: '订单号', key: 'orderId', width: 40 },
    { header: '金额', key: 'amount', width: 13, style: YUAN },
  ]
  sheet.getRow(1).font = { bold: true }

  rows.forEach((row, i) => {
    sheet.addRow({
      index: i + 1,
      time: new Date(row.time).toLocaleString('zh-CN'),
      kind: KIND_LABEL[row.kind],
      orderCategory: ORDER_TYPE_LABEL[row.orderCategory] ?? row.orderCategory,
      orderId: row.orderId,
      amount: row.amount / 100,
    })
  })

  const totalRow = sheet.addRow({
    index: '合计',
    time: `共 ${rows.length} 笔`,
    amount: rows.reduce((sum, r) => sum + r.amount, 0) / 100,
  })
  totalRow.font = { bold: true }

  const buffer = await workbook.xlsx.writeBuffer()
  const url = URL.createObjectURL(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
  )
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}
